import React, { useState, useEffect, Component } from 'react';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { AssetProvider, useAsset } from '../context/AssetContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, Monitor, Users, FolderKanban, Settings, 
  ClipboardList, LogOut, ChevronDown, User, AlertTriangle, RefreshCw 
} from 'lucide-react';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

// Tab Components
import AssetDashboard from '../components/asset/AssetDashboard';
import AssetList from '../components/asset/AssetList';
import AssetDetail from '../components/asset/AssetDetail';
import EmployeeList from '../components/asset/EmployeeList';
import EmployeeDetail from '../components/asset/EmployeeDetail';
import ProjectList from '../components/asset/ProjectList';
import AssetSettings from '../components/asset/AssetSettings';
import AssetAuditLogs from '../components/asset/AssetAuditLogs';

// Error Boundary
class AssetManagerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AssetManager Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              The Asset Manager encountered an error. Please try refreshing the page.
            </p>
            <p className="text-sm text-red-500 mb-4 font-mono bg-red-50 p-2 rounded">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <Button 
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reload App
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AssetManagerContent = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [assetListFilters, setAssetListFilters] = useState(null);
  const { fetchLOVs, isReady } = useAsset();

  // Set page title and favicon
  usePageMeta(PAGE_META.ASSET_MANAGER.title, PAGE_META.ASSET_MANAGER.favicon);

  useEffect(() => {
    if (isReady) {
      fetchLOVs();
    }
  }, [isReady, fetchLOVs]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'assets', label: 'Assets', icon: Monitor },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'projects', label: 'Projects', icon: FolderKanban },
    { id: 'settings', label: 'List of Values', icon: Settings },
    { id: 'audit', label: 'Audit Logs', icon: ClipboardList },
  ];

  const handleLogout = () => {
    window.close();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="asset-manager-app">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center px-4 gap-4">
          <div className="flex items-center gap-2">
            <HarmonyLogoCompact size={32} />
            <span className="font-semibold text-lg hidden sm:inline">Asset Manager</span>
          </div>
          
          <div className="flex-1" />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline">Admin</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Close App
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== 'assets') { setSelectedAssetId(null); setAssetListFilters(null); } if (v !== 'employees') { setSelectedEmployeeId(null); } }} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
            {tabs.map((tab) => (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id} 
                className="gap-2"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <AssetDashboard onNavigate={(tab, filters) => { setActiveTab(tab); if (tab === 'assets') { setSelectedAssetId(null); setAssetListFilters(filters || null); } }} />
          </TabsContent>

          <TabsContent value="assets" className="space-y-6">
            {selectedAssetId ? (
              <AssetDetail
                assetId={selectedAssetId}
                onBack={() => setSelectedAssetId(null)}
                onViewEmployee={(empId) => { setActiveTab('employees'); setSelectedEmployeeId(empId); }}
              />
            ) : (
              <AssetList
                onView={(asset) => setSelectedAssetId(asset.asset_id)}
                initialFilters={assetListFilters}
                onClearInitialFilters={() => setAssetListFilters(null)}
              />
            )}
          </TabsContent>

          <TabsContent value="employees" className="space-y-6">
            {selectedEmployeeId ? (
              <EmployeeDetail
                employeeId={selectedEmployeeId}
                onBack={() => setSelectedEmployeeId(null)}
                onViewAsset={(assetId) => { setActiveTab('assets'); setSelectedAssetId(assetId); }}
              />
            ) : (
              <EmployeeList onView={(emp) => setSelectedEmployeeId(emp.employee_id)} />
            )}
          </TabsContent>

          <TabsContent value="projects" className="space-y-6">
            <ProjectList />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <AssetSettings />
          </TabsContent>

          <TabsContent value="audit" className="space-y-6">
            <AssetAuditLogs />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const AssetManagerApp = () => {
  // Signed in from the visitor's own session (see useSessionLaunch); the role
  // comes from that session too, not from the URL.
  const { token, role } = useSessionLaunch();

  return (
    <AssetManagerErrorBoundary>
      <AssetProvider token={token} role={role}>
        <AssetManagerContent />
      </AssetProvider>
    </AssetManagerErrorBoundary>
  );
};

export default AssetManagerApp;
